/* =============================================================================
   kfz-plate-layout.js - lays out the writable plate (.ss-editable) after the
   measurements of the reference. Shared by the configurator section and the
   cart section, so the cart shows exactly the plate the customer configured.

   Needs kfz-districts.js, kfz-config.js and kfz-validate.js before it.

   apply(editable, state, options)
     editable : the .ss-editable node with its fields (see the markup in
                sections/kfz-konfigurator.liquid or snippets/kfz-cart-plate)
     state    : { cityCode, letters, digits, parkingText, sizeId, type,
                  seasonFrom, seasonTo } - the field values are read from the
                inputs themselves, so they must already be filled in
     options  : { finish: 'carbon' | 'normal' }
   Returns true when a field had to be shortened to fit the chosen size.
   ========================================================================== */
var KfzPlateLayout = (function (PlateConfig, PlateValidate) {
  'use strict';


  // Auto: Masse der Vorlage (sofortschild.de, am 15.09.2026 im Browser
  // ausgemessen, siehe Auto-Kennzeichen-Vorschau-Messungen). Die Vorlage
  // zeichnet 1.1 px je mm, deshalb sind alle Werte dort durch 1.1
  // geteilt. Sie gelten fuer alle acht Autogroessen gleich und zaehlen ab
  // der Aussenkante des schwarzen Rahmens, der 2.727 innerhalb der
  // Plattenkante liegt. Die Vorlage laesst die Zeichen nie schrumpfen:
  // 90.2 px in jeder Groesse, dafuer nimmt sie der Erkennungsnummer
  // Zeichen weg. Feldbreite nach der Zeichenzahl im Feld (in der Vorlage
  // die Klassen w1 bis w4).
  const AUTO = {
    ring: 2.727,          // weisser Aussenrand
    padL: 59.631,         // Rahmen 3.636 + 56 Innenabstand links
    padR: 9.636,          // Rahmen 3.636 + 6 Innenabstand rechts
    rowY: 11.278, rowH: 81.989, font: 82,
    fieldW: [0, 59.489, 98.992, 135.995, 178.495],  // 1-4 Zeichen
    seal: 30, sealH: 60.497,   // Gruppe: TUEV oben, Landessiegel unten
    suffixW: 41.406, suffixH: 67.273,               // E / H
    seasonW: 41.662, seasonH: 81.08,
    tailGap: 8,           // rechter Abstand an E/H und am Saisonblock
    pad2: 6.634,          // zweizeilig: Rahmen 3.636 + 3 Innenabstand
    euro2: 50,            // zweizeilig: Zeilenabstand fuer das Eurofeld
    topY: 6.634, botY: 105.924,
    sealGap2: 15          // zweizeilig: Ortsfeld zur Siegelgruppe
  };
  // Motorrad: Masse der Vorlage (sofortschild.de, am 15.09.2026 im Browser
  // ausgemessen, siehe Motorrad-Vorschau-Messungen). Die Vorlage zeichnet
  // 1.1 px je mm, deshalb sind alle Werte dort durch 1.1 geteilt. Sie
  // gelten fuer 180, 200 und 220 gleich und zaehlen ab der Aussenkante
  // des schwarzen Rahmens, der 2.727 innerhalb der Plattenkante liegt.
  // Der Rahmen hat keinen border, sondern einen Schatten nach innen: Chrome
  // rundet Rahmenbreiten auf ganze Pixel ab, aus 4 wurden sonst 3 px.
  // Die Groessen im CSS (Rahmen, Eurofeld, Monate) stammen aus derselben
  // Messung.
  const MOTO = {
    ring: 2.727,          // weisser Aussenrand
    pad: 6.633,           // Rahmen 3.636 + Innenabstand 2.997
    euro: 50,             // Platz fuer das Eurofeld in der oberen Reihe
    rowH: 60, font: 65,   // Feldhoehe und Schriftgroesse
    topY: 6.633, bottomY: 127.911,
    fieldW: [0, 41.989, 75, 106.989, 140],   // Feldbreite fuer 1-4 Zeichen
    suffixW: 29.389, suffixH: 52.003,        // E / H
    sealX: 92.131, sealY: 82.272, seal: 30,  // Landessiegel; die TUEV-Plakette
                                             // davor bleibt als leerer Platz
    seasonW: 25.384, seasonH: 44.659, seasonY: 74.943,
    slack: 1              // so viel darf die untere Reihe ueberlaufen
  };
  // Leichtkraftrad: Masse der Vorlage (sofortschild.de, am 15.09.2026 im
  // Browser ausgemessen, siehe Leichtkraftrad-Vorschau-Messungen). Auch
  // hier zeichnet die Vorlage 1.1 px je mm, alle Werte sind dadurch
  // geteilt; sie gelten fuer beide Groessen gleich und zaehlen ab der
  // Aussenkante des schwarzen Rahmens. Nicht das Motorradmass benutzen:
  // das Leichtkraftrad hat zwei Reihen statt drei, das Siegel neben dem
  // Ortskuerzel statt darunter, andere Feldbreiten und eine andere
  // Verteilung der unteren Reihe.
  const LKR = {
    ring: 2.727,          // weisser Aussenrand
    pad: 7.628,           // Rahmen 3.636 + Innenabstand 3.992
    euro: 30,             // Platz fuer das Eurofeld in der oberen Reihe
    rowH: 50, font: 53,   // Feldhoehe 55 px, Schrift 58.3 px
    topY: 7.628,
    bottomY: 66.917, bottomH: 50,           // untere Reihe ohne Saison
    seasonRowY: 64.73, seasonRowH: 52.187,  // und mit Saison
    fieldW: [0, 38, 65, 90],                // Feldbreite fuer 1-3 Zeichen
    suffixW: 28.992, suffixH: 47.273,       // E / H
    tailGap: 5.6,         // rechter Abstand an E/H und am Saisonblock
    sealGap: 15,          // Ortsfeld zur Siegelgruppe
    sealGroupW: 60.497,   // Landessiegel 30, Luecke 7.5, TUEV 23 - der
    seal: 30,             // TUEV bleibt leerer, reservierter Platz
    seasonW: 29.162, seasonH: 52.187
  };
  // Parkplatzschild: Masse der Vorlage (sofortschild.de, 15.09.2026, siehe
  // Parkplatz-Vorschau-Messungen), px durch 1.1, ab der Aussenkante des
  // schwarzen Rahmens. Das Feld ist im weissen Bereich rechts vom blauen
  // Feld zentriert und bleibt fuer jeden Text gleich gross.
  const PARK = {
    plateH: 110,          // Vorlage: 572 x 121 px, also 520 x 110
    fieldX: 59.631, fieldY: 11.278, fieldW: 445.284, fieldH: 81.989,
    font: 82              // 90.2 px
  };

  function apply(editable, state, options) {
    options = options || {};
    const type = PlateConfig.getType(state.type) || PlateConfig.TYPES[0];
    const size = PlateConfig.getSize(state.sizeId) || PlateConfig.SIZES[0];
  editable.dataset.two = String(!!size.twoLine);
  editable.dataset.sealbelow = String(!!size.sealBelowCity);
  editable.dataset.lightbike = String(!!size.lightBike);
  editable.dataset.auto = String(!size.sealBelowCity && !size.parking && !size.lightBike);
  editable.dataset.parking = String(!!size.parking);
  editable.dataset.extra = String(!!(type.suffix || type.isSeason));
  editable.dataset.season = String(!!type.isSeason);
    editable.dataset.finish = options.finish === 'carbon' ? 'carbon' : 'normal';
  // Die Aussenmasse der Platte schliessen den weissen Rand mit ein,
  // der als Schatten um den schwarzen Rahmen liegt - die Vorlage
  // zeichnet ihn innerhalb der Aussenbox, wir ausserhalb. Beim
  // Parkplatzschild dazu die Hoehe, die die Vorlage zeichnet.
  const ring = 2 * AUTO.ring;
  const plateH = size.parking ? PARK.plateH : size.h;
  // previewWidth: die Breite, die die Vorlage zeichnet, wo sie von der
  // verkauften abweicht - siehe config.js. Ohne Angabe gilt w.
  const plateW = size.previewWidth || size.w;
  // Die Box, die syncPlate bemasst, ist der schwarze Rahmen - der
  // weisse Rand liegt als Schatten darum.
  const frameW = plateW - ring;
  editable.style.width = ((plateW - ring) / 520 * 100) + '%';
  editable.style.height = 'calc(' + (plateH - ring) + ' * var(--u))';
  editable.style.aspectRatio = 'auto';
  const cityInput = editable.querySelector('.kfz-input--city');
  const letterInput = editable.querySelector('.kfz-input--letters');
  const digitInput = editable.querySelector('.kfz-input--digits');
  const sealNode = editable.querySelector('.ss-seals');
  const suffixNode = editable.querySelector('.ss-suffix');
  const monthsNode = editable.querySelector('.ss-months');
  const parkingInput = editable.querySelector('.kfz-input--parking');
  // State seal under the city code: the district list maps the code to
  // its Bundesland, whose icon is a bare filename resolved against the
  // theme's asset CDN. Unknown codes or states without an icon keep the
  // plain grey circle.
  const district = state.cityCode ? PlateValidate.lookupDistrict(state.cityCode) : null;
  const sealUrl = district ? PlateConfig.assetUrl(district.state.iconUrl) : '';
  sealNode.firstElementChild.style.backgroundImage = sealUrl ? 'url("' + sealUrl + '")' : '';
  if (sealUrl) { sealNode.dataset.state = district.state.id; } else { delete sealNode.dataset.state; }
  // Bringt die Groesse eigene Obergrenzen mit (die Motorradschilder tun
  // das), geben sie auch den Platzhalter vor - so bleibt beides an
  // einer Stelle in config.js und kann hier nicht auseinanderlaufen.
  // Auf einem E- oder H-Schild kostet das Suffix eine Zeichenbreite,
  // deshalb bringt die Groesse dafuer eigene Grenzen mit.
  var letterCap = (type.suffix && size.maxLettersSuffix) ? size.maxLettersSuffix : size.maxLetters;
  var digitCap = (type.suffix && size.maxDigitsSuffix) ? size.maxDigitsSuffix : size.maxDigits;
  // Das Unterscheidungszeichen hat drei Zeichen, solange sie passen.
  // Nur das Auto nimmt davon etwas weg, und erst, wenn Ziffern und
  // Buchstaben schon auf einem Zeichen stehen.
  var cityCap = 3;
  if (size.sealBelowCity) {
    // Motorrad: die Felder sind so breit wie in der Vorlage, und die
    // untere Reihe muss sie nebeneinander fassen. Buchstaben und
    // Ziffern teilen sich den Platz - was das eine Feld belegt, fehlt
    // dem anderen. Leere Felder zaehlen mit ihrem Platzhalter (ein
    // Zeichen). Die Vorlage laesst hier Ueberlaeufe zu, wir nicht.
    const room = size.w - 2 * MOTO.ring - 2 * MOTO.pad + MOTO.slack - (type.suffix ? MOTO.suffixW : 0);
    const fits = (letters, digits) => MOTO.fieldW[letters] + MOTO.fieldW[digits] <= room;
    const typedDigits = Math.max(1, Math.min(digitInput.value.length, digitCap));
    while (letterCap > 1 && !fits(letterCap, typedDigits)) { letterCap--; }
    const typedLetters = Math.max(1, Math.min(letterInput.value.length, letterCap));
    while (digitCap > 1 && !fits(typedLetters, digitCap)) { digitCap--; }
    letterInput.placeholder = 'A';
    digitInput.placeholder = '1234'.slice(0, digitCap);
  } else if (size.lightBike) {
    // Die Vorlage nimmt der Erkennungsnummer je ein Zeichen ab, sobald
    // ein E oder H und sobald der Saisonblock danebensteht: 123, mit
    // einem von beiden 12, mit beiden 1. Das Feld ist dann auch genau
    // so breit - es haengt an der erlaubten, nicht an der getippten
    // Laenge (Vorlage: die Klassen w1 bis w3).
    letterCap = 2;
    digitCap = Math.max(1, 3 - (type.suffix ? 1 : 0) - (type.isSeason ? 1 : 0));
    letterInput.placeholder = 'AB';
    digitInput.placeholder = '123'.slice(0, digitCap);
  } else {
    // Auto: die Vorlage macht die Zeichen nie kleiner. Passt eine Reihe
    // nicht, nimmt sie ihr Zeichen weg - erst den Ziffern, dann den
    // Buchstaben, zuletzt dem Ortskuerzel. Ausgangspunkt ist die
    // Ziffernzahl, die die Groesse ohne Zusatz traegt (die maxlength
    // der Vorlage, in config.js als maxDigits); E/H und der
    // Saisonblock kosten davon.
    const box = size.w - 2 * AUTO.ring;
    const tail = type.suffix ? AUTO.suffixW + AUTO.tailGap : 0;
    const seasonRoom = type.isSeason ? AUTO.seasonW + AUTO.tailGap : 0;
    // Zweizeilig stehen Buchstaben und Ziffern allein in der unteren
    // Zeile; Ortskuerzel, Siegel und Monate teilen sich die obere.
    const room = size.twoLine
      ? box - 2 * AUTO.pad2 - tail
      : box - AUTO.padL - AUTO.padR - AUTO.seal - tail - seasonRoom;
    // Das Ortsfeld ist so breit wie das, was darin steht, die beiden
    // anderen so breit wie ihre Grenze - ihr Platzhalter ist genau so
    // lang. Deshalb zaehlt hier das getippte Ortskuerzel und nicht die
    // Grenze: mit KA bleiben drei Ziffern, mit HDH nicht.
    const cityLen = () => Math.min(cityCap, Math.max(1, (cityInput.value || cityInput.placeholder).length));
    const fits = () => (size.twoLine ? 0 : AUTO.fieldW[cityLen()]) +
      AUTO.fieldW[letterCap] + AUTO.fieldW[digitCap] <= room;
    while (digitCap > 1 && !fits()) { digitCap--; }
    while (letterCap > 1 && !fits()) { letterCap--; }
    while (cityCap > 1 && !fits()) { cityCap--; }
    if (size.twoLine && type.isSeason) {
      // Oben teilt sich das Ortskuerzel die Zeile mit Siegel und
      // Monaten - auf der 280er bleibt dafuer kein drittes Zeichen.
      const topRoom = box - 2 * AUTO.pad2 - AUTO.euro2 - AUTO.sealGap2 -
        AUTO.seal - AUTO.tailGap - AUTO.seasonW;
      while (cityCap > 1 && AUTO.fieldW[cityCap] > topRoom) { cityCap--; }
    }
    letterInput.placeholder = 'AB'.slice(0, letterCap);
    digitInput.placeholder = '1234'.slice(0, digitCap);
  }
  // The placeholders double as the limits for single-line plates. When a
  // smaller size no longer fits what was typed, keep the leading
  // characters and tell the customer. Two-line plates keep 2 letters.
  const limits = [[cityInput, cityCap],
                  [letterInput, letterCap || (size.twoLine ? 2 : letterInput.placeholder.length)],
                  [digitInput, digitCap || digitInput.placeholder.length]];
  let trimmed = false;
  limits.forEach(([input, limit]) => {
    input.maxLength = limit;
    if (input.value.length > limit) { input.value = input.value.slice(0, limit); trimmed = true; }
  });
  suffixNode.textContent = type.suffix || '';
  monthsNode.children[0].textContent = state.seasonFrom || '00';
  monthsNode.children[1].textContent = state.seasonTo || '00';
  function place(node, x, y, w, h, font) {
    Object.entries({x,y,w,h}).forEach(([key,value])=>node.style.setProperty('--'+key,value));
    if (font) node.style.setProperty('--font',font);
  }
  // Auto: das Feld ist so breit wie die Zeichen, die darin stehen -
  // leer so breit wie sein Platzhalter (Vorlage: die Klassen w1 bis w4).
  function autoField(node) {
    return AUTO.fieldW[Math.min(4, Math.max(1, (node.value || node.placeholder).length))];
  }
  // Verteilt eine Reihe so, wie es die Vorlage tut: einzeilig wie
  // justify-content:space-between (Luecken nur zwischen den Feldern),
  // die untere Zeile der zweizeiligen wie space-around (aussen eine
  // halbe Luecke, dazwischen eine ganze). E/H und der Saisonblock
  // tragen einen rechten Abstand, der mitzaehlt, aber nicht zum
  // Kasten gehoert. Jedes Feld sitzt mittig in der Reihe.
  function spread(items, left, width, y, rowH, around) {
    const used = items.reduce((sum, item) => sum + item[1], 0);
    const slots = around ? 2 * items.length : Math.max(1, items.length - 1);
    const gap = Math.max(0, (width - used) / slots);
    let x = left + (around ? gap : 0);
    items.forEach(([node, w, h, font, tail]) => {
      place(node, x, y + (rowH - h) / 2, w - (tail || 0), h, font);
      x += w + (around ? 2 * gap : gap);
    });
  }
  if (size.parking) {
    // Parkplatzschild: ein blaues Feld mit P und eine einzige freie
    // Zeile, beides mit den Massen der Vorlage (siehe PARK).
    place(parkingInput, PARK.fieldX, PARK.fieldY, PARK.fieldW, PARK.fieldH, PARK.font);
  } else if (size.sealBelowCity) {
    // Motorrad: drei Reihen - Stadt, Siegel, Nummer - mit den Massen der
    // Vorlage (siehe MOTO). Jedes Feld ist so breit wie seine
    // Zeichenzahl, mindestens so breit wie der Platzhalter.
    //
    //   Reihe 1  y   6.6  Hoehe 60    Stadt mittig rechts vom Eurofeld
    //   Reihe 2  y  82.3  Hoehe 30    Siegel fest bei x 92.1
    //   Reihe 3  y 127.9  Hoehe 60    Buchstaben links, Ziffern rechts,
    //                                 E/H ganz rechts, Abstaende gleich
    //   Saison   y  74.9  Hoehe 44.7  rechts auf der Siegelzeile
    const W = size.w - 2 * MOTO.ring;
    const left = MOTO.pad;
    const right = W - MOTO.pad;
    const fieldW = node => MOTO.fieldW[Math.min(4, Math.max(1, node.value.length, node.placeholder.length))];
    const cityW = fieldW(cityInput);
    place(cityInput, (left + MOTO.euro + right) / 2 - cityW / 2, MOTO.topY, cityW, MOTO.rowH, MOTO.font);
    place(sealNode, MOTO.sealX, MOTO.sealY, MOTO.seal, MOTO.seal);
    if (type.isSeason) { place(monthsNode, right - MOTO.seasonW, MOTO.seasonY, MOTO.seasonW, MOTO.seasonH); }
    // Wie justify-content: space-between in der Vorlage.
    const items = [[letterInput, fieldW(letterInput), MOTO.rowH], [digitInput, fieldW(digitInput), MOTO.rowH]];
    if (type.suffix) { items.push([suffixNode, MOTO.suffixW, MOTO.suffixH]); }
    const used = items.reduce((sum, item) => sum + item[1], 0);
    const gap = Math.max(0, (right - left - used) / (items.length - 1));
    let x = left;
    items.forEach(([node, w, h]) => {
      place(node, x, MOTO.bottomY + (MOTO.rowH - h) / 2, w, h, MOTO.font);
      x += w + gap;
    });
  } else if (size.lightBike) {
    // Leichtkraftrad: zwei Reihen mit den Massen der Vorlage (LKR).
    //
    //   Reihe 1  y  7.6  Hoehe 50   Ortskuerzel und Siegelgruppe als
    //                               ein Block, mittig im Platz rechts
    //                               vom Eurofeld
    //   Reihe 2  y 66.9  Hoehe 50   Buchstaben und Ziffern, dahinter
    //                               E/H und der Saisonblock
    //   Mit Saison faengt die untere Reihe bei 64.7 an und wird 52.2
    //   hoch; alles darin bleibt mittig zur Reihe.
    const W = plateW - 2 * LKR.ring;
    const left = LKR.pad;
    const right = W - LKR.pad;
    const fieldW = node => LKR.fieldW[Math.min(3, Math.max(1, node.value.length, node.placeholder.length))];
    const cityW = fieldW(cityInput);
    const groupW = cityW + LKR.sealGap + LKR.sealGroupW;
    let x = (left + LKR.euro + right) / 2 - groupW / 2;
    place(cityInput, x, LKR.topY, cityW, LKR.rowH, LKR.font);
    place(sealNode, x + cityW + LKR.sealGap, LKR.topY + (LKR.rowH - LKR.seal) / 2, LKR.sealGroupW, LKR.seal);
    // Untere Reihe wie justify-content: space-around, also eine halbe
    // Luecke aussen und eine ganze zwischen den Feldern. E/H und der
    // Saisonblock tragen in der Vorlage einen rechten Abstand, der
    // dabei mitzaehlt, aber nicht zum Kasten selbst gehoert.
    const rowY = type.isSeason ? LKR.seasonRowY : LKR.bottomY;
    const rowH = type.isSeason ? LKR.seasonRowH : LKR.bottomH;
    const items = [[letterInput, fieldW(letterInput), LKR.rowH, LKR.font],
                   [digitInput, fieldW(digitInput), LKR.rowH, LKR.font]];
    if (type.suffix) { items.push([suffixNode, LKR.suffixW + LKR.tailGap, LKR.suffixH, 0, LKR.tailGap]); }
    if (type.isSeason) { items.push([monthsNode, LKR.seasonW + LKR.tailGap, LKR.seasonH, 0, LKR.tailGap]); }
    const used = items.reduce((sum, item) => sum + item[1], 0);
    const side = Math.max(0, (right - left - used) / (2 * items.length));
    x = left + side;
    items.forEach(([node, w, h, font, gap]) => {
      place(node, x, rowY + (rowH - h) / 2, w - (gap || 0), h, font);
      x += w + 2 * side;
    });
  } else if (size.twoLine) {
    // Auto zweizeilig, Masse der Vorlage (siehe AUTO).
    //
    //   Zeile 1  y   6.6  Hoehe 82   Ortskuerzel und Siegelgruppe,
    //                                zusammen mittig im Platz rechts
    //                                vom Eurofeld
    //   Zeile 2  y 105.9  Hoehe 82   Buchstaben und Ziffern wie
    //                                space-around, E/H dahinter
    //   Mit Saison haengt der Monatsblock rechts an der oberen Zeile;
    //   Ortskuerzel und Siegel ruecken dann an deren linken Rand.
    const lineW = frameW - 2 * AUTO.pad2;
    const topLeft = AUTO.pad2 + AUTO.euro2;
    const topW = lineW - AUTO.euro2;
    const cityW = autoField(cityInput);
    const sealY = AUTO.topY + (AUTO.rowH - AUTO.sealH) / 2;
    let cityX = topLeft + (topW - (cityW + AUTO.sealGap2 + AUTO.seal)) / 2;
    if (type.isSeason) {
      cityX = topLeft;
      place(monthsNode, topLeft + topW - AUTO.tailGap - AUTO.seasonW,
        AUTO.topY + (AUTO.rowH - AUTO.seasonH) / 2, AUTO.seasonW, AUTO.seasonH);
    }
    place(cityInput, cityX, AUTO.topY, cityW, AUTO.rowH, AUTO.font);
    place(sealNode, cityX + cityW + AUTO.sealGap2, sealY, AUTO.seal, AUTO.sealH);
    const bottom = [[letterInput, autoField(letterInput), AUTO.rowH, AUTO.font],
                    [digitInput, autoField(digitInput), AUTO.rowH, AUTO.font]];
    if (type.suffix) { bottom.push([suffixNode, AUTO.suffixW + AUTO.tailGap, AUTO.suffixH, 0, AUTO.tailGap]); }
    spread(bottom, AUTO.pad2, lineW, AUTO.botY, AUTO.rowH, true);
  } else {
    // Auto einzeilig, Masse der Vorlage (siehe AUTO): eine Reihe bei
    // y 11.3, Hoehe 82, von x 59.6 bis zur Breite minus 9.6. Darin
    // Ortskuerzel, Siegelgruppe, Buchstaben, Ziffern, dahinter E/H und
    // der Saisonblock - verteilt wie space-between.
    const items = [[cityInput, autoField(cityInput), AUTO.rowH, AUTO.font],
                   [sealNode, AUTO.seal, AUTO.sealH, 0],
                   [letterInput, autoField(letterInput), AUTO.rowH, AUTO.font],
                   [digitInput, autoField(digitInput), AUTO.rowH, AUTO.font]];
    if (type.suffix) { items.push([suffixNode, AUTO.suffixW + AUTO.tailGap, AUTO.suffixH, 0, AUTO.tailGap]); }
    if (type.isSeason) { items.push([monthsNode, AUTO.seasonW + AUTO.tailGap, AUTO.seasonH, 0, AUTO.tailGap]); }
    spread(items, AUTO.padL, frameW - AUTO.padL - AUTO.padR, AUTO.rowY, AUTO.rowH, false);
  }
  return trimmed;
  }

  return { apply: apply };
})(PlateConfig, PlateValidate);
